import { capabilityPhone, checkPhoneLine } from './phoneChannelApi';
import { entryValueForSave, cleanEntries, primaryEntry } from './contactMethods';
import { timeZoneFieldsForSave } from './contactTimeZone';

// BL-177 PR C0: ANYONE with access can add a company contact or account, after a duplicate check (any matching E.164 phone first,
// then any matching email). Editing an existing one is for admins only (RLS), so adding is one pass: account, person, entries.

/** Last word of a name: a Personal account is named after the person's last name. */
export const lastNameOf = (name) => (String(name || '').trim().split(/\s+/).filter(Boolean).pop() || '');

/** What the "who is this account" box should be called for an account type. */
export const accountNameLabel = (kind) => (kind === 'personal' ? 'Account name (household)' : 'Company name');

/**
 * Locked hotfix: a Personal account has no separate name box. It is named after the person (first + last name), so the person's
 * own Last name field is the only "Last name" on the form and always saves to the contact.
 */
export const personalAccountNameOf = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).join(' ');

/** A typed name made safe for one ILIKE: '%', '_' and '\' are literal, and '*' (which PostgREST reads as '%') matches one character. */
export const accountNameLookupPattern = (name) => String(name || '').replace(/[\\%_]/g, character => `\\${character}`).replace(/\*/g, '_');

const sameAccountName = (stored, typed) => String(stored || '').trim().toLowerCase() === String(typed || '').trim().toLowerCase();

export async function findDuplicateContacts(client, { entityId, phones, emails }) {
    const phoneValues = cleanEntries(phones, 'phone').map(e => e.value);
    const emailValues = cleanEntries(emails, 'email').map(e => e.value);
    if (!entityId || (!phoneValues.length && !emailValues.length)) return [];
    const { data, error } = await client.rpc('crm_find_company_contacts', {
        p_entity: entityId, p_phones: phoneValues, p_emails: emailValues,
    });
    if (error) throw error;
    const byContact = new Map();
    for (const row of data || []) {
        const existing = byContact.get(row.contact_id) || { contact_id: row.contact_id, name: row.name, matches: [] };
        existing.matches.push({ on: row.matched_on, value: row.matched_value });
        byContact.set(row.contact_id, existing);
    }
    return [...byContact.values()];
}

/**
 * Add an account and its first person with every phone / email / address entry.
 * BL-177 Step 2m: no communication preferences or consent columns are written. They belong to the client (the database resets them
 * to the defaults on a staff insert), so a new contact starts on the defaults until the client sets their own.
 * Returns { duplicates } and writes nothing when a duplicate is found and `allowDuplicate` is not set.
 */
export async function addCompanyClient(client, { form, allowDuplicate = false }) {
    const kind = form.account_kind === 'personal' ? 'personal' : 'business';
    // Val's typed-name rule covers the Account (company) name only. A Personal account is named after the person and is never looked up.
    const typedAccountName = kind === 'business' ? (form.account_name || '').trim() : '';
    const accountName = kind === 'personal' ? personalAccountNameOf(form.contact_name) : typedAccountName;
    if (!accountName) throw new Error(kind === 'personal' ? 'Enter the person’s name before saving.' : 'Enter a company name, or choose Personal for an individual contact.');
    if (!form.entity_id) throw new Error('Choose a booking node before saving.');
    if (!allowDuplicate) {
        const duplicates = await findDuplicateContacts(client, { entityId: form.entity_id, phones: form.phones, emails: form.emails });
        if (duplicates.length) return { duplicates };
    }

    let account = null;
    if (kind === 'business') {
        // Resolve the org from the selected booking node, never from a caller-supplied tenant id.
        const { data: entity, error: entityError } = await client.from('entities').select('org_id').eq('id', form.entity_id).single();
        if (entityError || !entity?.org_id) throw new Error('Could not check the account name. Please try again.');
        // Only live Business accounts can be reused: never a Personal one, and never one merged away (hidden from the CRM list).
        const { data: candidates, error: lookupError } = await client.from('accounts').select('id, account_name, created_at, status, merged_into_id')
            .eq('org_id', entity.org_id).eq('account_kind', 'business').is('merged_into_id', null).neq('status', 'merged')
            .ilike('account_name', accountNameLookupPattern(typedAccountName))
            .order('created_at', { ascending: true }).order('id', { ascending: true });
        if (lookupError) throw new Error('Could not check the account name. Please try again.');
        // The database narrows the list; the exact (trimmed, case-insensitive) match is decided here, oldest first.
        account = (candidates || []).find(row => row.status !== 'merged' && !row.merged_into_id && sameAccountName(row.account_name, typedAccountName)) || null;
    }
    if (!account?.id) {
        const { data: created, error: accountError } = await client.from('accounts').insert([{
            entity_id: form.entity_id,
            account_name: accountName,
            account_kind: kind,
            requires_human_review: !!form.requires_human_review,
            source: 'Staff entry',
            status: 'draft',
        }]).select('id').single();
        if (accountError) throw accountError;
        if (!created?.id) throw new Error('Could not create the account. Please try again.');
        account = created;
    }

    if ((form.contact_name || '').trim()) {
        const { data: contact, error: contactError } = await client
            .from('contacts')
            .insert([{
                entity_id: form.entity_id,
                name: form.contact_name.trim(),
                ...(form.contact_first_name !== undefined ? { first_name: form.contact_first_name.trim(), last_name: (form.contact_last_name || '').trim() } : {}),
                position: form.position?.trim() || null,
                primary_account_id: account.id,   // the database links the person to the account and makes them its primary contact
                requires_human_review: !!form.requires_human_review,
                source: 'Staff entry',
                // BL-177 Step 2j: the zone from the Time zone field - a person's pick, else suggested from the address / phone
                ...timeZoneFieldsForSave({
                    time_zone: form.time_zone, time_zone_source: form.time_zone_source,
                    addresses: cleanEntries(form.addresses, 'address'), phones: cleanEntries(form.phones, 'phone'),
                }),
                // BL-177 Step 2m: no preference or consent columns here: the client sets their own.
            }])
            .select('id')
            .single();
        if (contactError) throw contactError;

        for (const [kind2, key] of [['phone', 'phones'], ['email', 'emails'], ['address', 'addresses']]) {
            const rows = cleanEntries(form[key], kind2);
            const ordered = [...rows.filter(r => r.is_primary), ...rows.filter(r => !r.is_primary)]; // the first one inserted is the Primary
            for (const entry of ordered) {
                const { error } = await client.from('contact_methods').insert([{
                    contact_id: contact.id, kind: kind2, label: entry.label, value: entry.value, is_primary: entry.is_primary,
                }]);
                if (error) throw error;
                // 1.12.212-beta: a newly added phone is checked once (mobile / landline / VoIP). Fire-and-forget: never blocks the save.
                if (kind2 === 'phone') checkPhoneLine(client, capabilityPhone(entry.value));
            }
        }
        return { accountId: account.id, contactId: contact.id };
    }
    return { accountId: account.id };
}

export const hasPrimaryPhone = (form) => Boolean(entryValueForSave('phone', primaryEntry(form.phones)?.value || ''));
